import type { LucideIcon } from 'lucide-react';
import {
  ArrowUpRight,
  Code2,
  ShoppingCart,
  Megaphone,
  PenTool,
  Layers3,
  Globe,
  Rocket,
  LineChart,
} from 'lucide-react';

import Display from '@/components/typography/Display';
import Button from '@/components/ui/Button';
import { Container, HeroSection, Section } from '@/components/ui/Section';
import { PortfolioGallery } from '@/components/ui/portfolio-gallery';

import { useLanguage } from '@/contexts/LanguageContext';
import { useLocalizedPath } from '@/lib/locale';

type Locale = 'en' | 'el';

type Metric = {
  value: string;
  label: string;
};

type HeroContent = {
  eyebrow: string;
  title: string;
  description: string;
  primaryCta: string;
  secondaryCta: string;
  metrics: Metric[];
  highlights: string[];
  cardEyebrow: string;
  cardTitle: string;
  cardDescription: string;
  cardBullets: string[];
};

type CaseStudyContent = {
  eyebrow: string;
  title: string;
  description: string;
  metrics: Metric[];
  bullets: string[];
  cta: string;
};

type ProcessStep = {
  title: string;
  description: string;
};

type ProofPoint = {
  title: string;
  description: string;
  icon: LucideIcon;
};

type ClosingContent = {
  title: string;
  description: string;
  highlights: string[];
  primary: string;
  secondary: string;
};

type ServiceKey = 'product' | 'commerce' | 'brand' | 'growth';

type ServiceMeta = {
  icon: LucideIcon;
  href: string;
};

type ServiceCopy = {
  title: string;
  description: string;
};

const heroCopy: Record<Locale, HeroContent> = {
  en: {
    eyebrow: 'PixelPro Solutions',
    title: 'Digital solutions for modern businesses.',
    description:
      'We design and build websites, e-shops, and web apps that support your business growth, from the first idea to continuous improvement.',
    primaryCta: 'Book a free consultation',
    secondaryCta: 'See our services',
    metrics: [
      { value: 'Work for real businesses', label: 'Across different industries and needs' },
      { value: 'Long-term partnerships', label: 'With clients who trust us again' },
      { value: 'Organized project kickoffs', label: 'No chaos or delays' },
    ],
    highlights: [
    ],
    cardEyebrow: 'How we work',
    cardTitle: 'How we work',
    cardDescription:
      'We collaborate in a simple, organized way without unnecessary processes. From the first conversation to delivery, you always know what is happening and when.',
    cardBullets: [
      'Clear plan and agreed steps',
      'Regular check-ins at key stages',
      'Handover with guidance so you can continue easily',
    ],
  },
  el: {
    eyebrow: 'PixelPro Solutions',
    title: 'Ψηφιακές λύσεις για σύγχρονες επιχειρήσεις.',
    description:
      'Σχεδιάζουμε και υλοποιούμε ιστοσελίδες, e-shop και web εφαρμογές που στηρίζουν την ανάπτυξη της επιχείρησής σας, από την πρώτη ιδέα έως τη συνεχή βελτίωση.',
    primaryCta: 'Κλείστε δωρεάν συμβουλευτική',
    secondaryCta: 'Δείτε τις υπηρεσίες μας',
    metrics: [
      { value: 'Έργα για πραγματικές επιχειρήσεις', label: 'Σε διαφορετικούς κλάδους και ανάγκες' },
      { value: 'Μακροχρόνιες συνεργασίες', label: 'Με πελάτες που μας εμπιστεύονται ξανά' },
      { value: 'Οργανωμένη εκκίνηση έργων', label: 'Χωρίς χάος και καθυστερήσεις' },
    ],
    highlights: [
    ],
    cardEyebrow: 'Πώς δουλεύουμε',
    cardTitle: 'Πώς δουλεύουμε',
    cardDescription:
      'Συνεργαζόμαστε με απλό και οργανωμένο τρόπο, χωρίς περιττές διαδικασίες. Από την πρώτη συζήτηση μέχρι την παράδοση, ξέρετε τι γίνεται και πότε.',
    cardBullets: [
      'Ξεκάθαρο πλάνο και συμφωνημένα βήματα',
      'Τακτική επικοινωνία στα βασικά στάδια',
      'Παράδοση με οδηγίες για να μπορείτε να συνεχίσετε εύκολα',
    ],
  },
};

const serviceOrder: ServiceKey[] = ['product', 'commerce', 'brand', 'growth'];

const serviceMeta: Record<ServiceKey, ServiceMeta> = {
  product: { icon: Code2, href: '/services/web-design' },
  commerce: { icon: ShoppingCart, href: '/services/ecommerce' },
  brand: { icon: PenTool, href: '/services/branding' },
  growth: { icon: Megaphone, href: '/services/marketing-strategy' },
};

const serviceCopy: Record<Locale, Record<ServiceKey, ServiceCopy>> = {
  en: {
    product: {
      title: 'Websites & Web Platforms',
      description:
        'Modern, fast, and usable websites built around your business needs.',
    },
    commerce: {
      title: 'E-shop & Online Sales',
      description:
        'Design and build e-shops that are easy to manage and friendly for customers.',
    },
    brand: {
      title: 'Brand & Content',
      description:
        'Structure, copy, and core identity so your business looks professional and trustworthy.',
    },
    growth: {
      title: 'Support & Improvements',
      description:
        'We improve, extend, and support your project whenever you need.',
    },
  },
  el: {
    product: {
      title: 'Ιστοσελίδες & Web Platforms',
      description:
        'Κατασκευή σύγχρονων ιστοσελίδων, γρήγορων και εύχρηστων, προσαρμοσμένων στις ανάγκες της επιχείρησής σας.',
    },
    commerce: {
      title: 'E-shop & Online Πωλήσεις',
      description:
        'Σχεδιασμός και υλοποίηση e-shop που είναι εύκολα στη διαχείριση και φιλικά για τον πελάτη.',
    },
    brand: {
      title: 'Brand & Περιεχόμενο',
      description:
        'Δομή, κείμενα και βασική ταυτότητα ώστε η επιχείρησή σας να δείχνει επαγγελματική και αξιόπιστη.',
    },
    growth: {
      title: 'Υποστήριξη & Βελτιώσεις',
      description:
        'Βελτιώνουμε, επεκτείνουμε και υποστηρίζουμε το έργο σας όταν το χρειαστείτε.',
    },
  },
};

const caseStudyCopy: Record<Locale, CaseStudyContent> = {
  en: {
    eyebrow: 'Case Studies',
    title: 'How we work in practice',
    description:
      'In every project, we follow clear steps: understand the need, design the solution, and deliver with control and clarity. In case studies we show real examples, our thinking, and the decisions we made at each stage.',
    metrics: [],
    bullets: [
      'What problem the business faced',
      'Which approach we followed',
      'How the solution was delivered in practice',
    ],
    cta: 'See all case studies →',
  },
  el: {
    eyebrow: 'Case Studies',
    title: 'Πώς δουλεύουμε στην πράξη',
    description:
      'Σε κάθε έργο, ακολουθούμε συγκεκριμένα βήματα: κατανόηση ανάγκης, σχεδιασμό λύσης και υλοποίηση με έλεγχο και σαφήνεια. Στις μελέτες περίπτωσης παρουσιάζουμε παραδείγματα από έργα που έχουμε αναλάβει, τον τρόπο σκέψης μας και τις αποφάσεις που πήραμε σε κάθε στάδιο.',
    metrics: [],
    bullets: [
      'Τι πρόβλημα είχε η επιχείρηση',
      'Ποια προσέγγιση ακολουθήσαμε',
      'Πώς υλοποιήθηκε η λύση στην πράξη',
    ],
    cta: 'Δείτε όλες τις μελέτες περίπτωσης →',
  },
};

const processCopy: Record<Locale, ProcessStep[]> = {
  en: [
    {
      title: 'Understanding & Analysis (Discovery)',
      description: 'We learn your business, your needs, and what you want to achieve with the project.',
    },
    {
      title: 'Design & Plan (Planning)',
      description: 'We define the solution, structure, and key elements before development begins.',
    },
    {
      title: 'Implementation (Development)',
      description: 'We build the project in stages, with review and regular updates.',
    },
    {
      title: 'Delivery & Support (Delivery)',
      description: 'We deliver the project, show you how to use it, and stay available for improvements.',
    },
  ],
  el: [
    {
      title: 'Κατανόηση & Ανάλυση (Discovery)',
      description: 'Μαθαίνουμε την επιχείρησή σας, τις ανάγκες σας και τι θέλετε να πετύχετε με το έργο.',
    },
    {
      title: 'Σχεδιασμός & Πλάνο (Planning)',
      description: 'Ορίζουμε τη λύση, τη δομή και τα βασικά στοιχεία του έργου πριν ξεκινήσει η υλοποίηση.',
    },
    {
      title: 'Υλοποίηση (Development)',
      description: 'Κατασκευάζουμε το έργο σταδιακά, με έλεγχο και τακτική ενημέρωση.',
    },
    {
      title: 'Παράδοση & Υποστήριξη (Delivery)',
      description: 'Παραδίδουμε το έργο, σας δείχνουμε πώς να το χρησιμοποιείτε και είμαστε διαθέσιμοι για βελτιώσεις.',
    },
  ],
};

const proofPoints: Record<Locale, ProofPoint[]> = {
  en: [
    {
      title: 'Transparency & communication',
      description:
        'From the start we agree on steps and timeline. Each project has a shared progress board so you always know what is happening.',
      icon: Layers3,
    },
    {
      title: 'Organized without the mess',
      description:
        'All files, comments, and decisions stay in one place, without lost emails or confusion.',
      icon: LineChart,
    },
    {
      title: 'Quality in delivery',
      description:
        'We review the work before it goes live and fix what is needed, so it ships right the first time.',
      icon: Rocket,
    },
    {
      title: 'Clear handover',
      description:
        'We deliver the work with basic guidance and the right access so you can manage it confidently.',
      icon: Globe,
    },
  ],
  el: [
    {
      title: 'Διαφάνεια & επικοινωνία',
      description:
        'Από την αρχή συμφωνούμε τα βήματα και το χρονοδιάγραμμα. Κάθε έργο έχει κοινό πίνακα προόδου, ώστε να ξέρετε πάντα τι γίνεται.',
      icon: Layers3,
    },
    {
      title: 'Οργάνωση χωρίς μπέρδεμα',
      description:
        'Όλα τα αρχεία, τα σχόλια και οι αποφάσεις βρίσκονται συγκεντρωμένα, χωρίς χαμένα emails ή σύγχυση.',
      icon: LineChart,
    },
    {
      title: 'Ποιότητα στην υλοποίηση',
      description:
        'Ελέγχουμε τη δουλειά πριν το live και διορθώνουμε όπου χρειάζεται, για να παραδίδεται σωστά από την πρώτη φορά.',
      icon: Rocket,
    },
    {
      title: 'Καθαρή παράδοση',
      description:
        'Παραδίδουμε το έργο με βασικές οδηγίες και πρόσβαση στα απαραίτητα, ώστε να μπορείτε να το διαχειρίζεστε άνετα.',
      icon: Globe,
    },
  ],
};

const statsStrip: Record<Locale, Metric[]> = {
  en: [
    { value: '4–6 weeks', label: 'Time to first release' },
    { value: 'Clear process', label: 'No surprises along the way' },
    { value: '92%', label: 'Partnerships that continue' },
  ],
  el: [
    { value: '4–6 εβδομάδες', label: 'Χρόνος μέχρι την πρώτη έκδοση' },
    { value: 'Ξεκάθαρη διαδικασία', label: 'Χωρίς εκπλήξεις στην πορεία' },
    { value: '92%', label: 'Συνεργασίες που συνεχίζονται' },
  ],
};

const closingCopy: Record<Locale, ClosingContent> = {
  en: {
    title: 'Want to see what your business needs?',
    description:
      'If your website or e-shop is not delivering the results you expect, we start with a short conversation to understand what is wrong and what can be improved.',
    highlights: [
      'No commitments or obligations',
      'A clear view of what is needed',
      'Practical recommendations, not generalities',
    ],
    primary: 'Book a free consultation',
    secondary: 'View the portfolio',
  },
  el: {
    title: 'Τι χρειάζεται η επιχείρησή σας;',
    description:
      'Αν η ιστοσελίδα ή το e-shop σας δεν φέρνει τα αποτελέσματα που περιμένετε, ξεκινάμε με μια σύντομη συζήτηση για να καταλάβουμε τι φταίει και τι μπορεί να βελτιωθεί.',
    highlights: [
      'Χωρίς δεσμεύσεις ή υποχρεώσεις',
      'Ξεκάθαρη εικόνα για το τι χρειάζεται',
      'Πρακτικές προτάσεις, όχι γενικότητες',
    ],
    primary: 'Κλείστε δωρεάν συμβουλευτική',
    secondary: 'Δείτε το portfolio',
  },
};

export default function HomePage() {
  const { language } = useLanguage();
  const locale: Locale = language === 'el' ? 'el' : 'en';
  const localizedPath = useLocalizedPath();

  const hero = heroCopy[locale];
  const services = serviceOrder.map((key) => ({
    key,
    icon: serviceMeta[key].icon,
    href: serviceMeta[key].href,
    ...serviceCopy[locale][key],
  }));
  const caseStudy = caseStudyCopy[locale];
  const process = processCopy[locale];
  const proof = proofPoints[locale];
  const stats = statsStrip[locale];
  const closing = closingCopy[locale];

  return (
    <main id="main-content" itemScope itemType="https://schema.org/WebPage">
      <HeroSection className="relative overflow-hidden pt-32 pb-20 md:pt-36 lg:pt-40 lg:pb-24">
        <div className="absolute inset-0 geometric-pattern" />
        <div className="absolute inset-0 bg-gradient-to-br from-pp-black/95 via-black/85 to-black" />

        <Container className="relative lg:min-h-[calc(100vh-220px)] lg:flex lg:flex-col lg:justify-center">
          <div className="grid grid-cols-1 gap-10 sm:gap-12 lg:gap-14 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)] lg:items-start">
            <div className="space-y-8">
              <div className="space-y-5">
                <p className="text-[11px] font-medium uppercase tracking-[0.4em] text-brand-highlight/70">
                  {hero.eyebrow}
                </p>
                <Display
                  variant="h1"
                  as="h1"
                  className="text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.12] tracking-tight"
                >
                  {hero.title}
                </Display>
              </div>

              <Display
                variant="subhead"
                as="p"
                className="max-w-2xl text-base sm:text-lg leading-relaxed text-white/70"
              >
                {hero.description}
              </Display>

              <div className="flex flex-col gap-4 sm:flex-row">
                <Button
                  as="button"
                  onClick={() => window.navigateToConsultation?.()}
                  trackCta={{ text: hero.primaryCta, section: 'home_hero', variant: 'primary' }}
                  variant="primary"
                  size="lg"
                  className="w-full justify-center sm:w-auto"
                >
                  {hero.primaryCta}
                </Button>
                <Button
                  as="a"
                  href={localizedPath('/services')}
                  trackCta={{ text: hero.secondaryCta, section: 'home_hero', variant: 'secondary' }}
                  variant="secondary"
                  size="lg"
                  className="w-full justify-center sm:w-auto"
                >
                  {hero.secondaryCta}
                </Button>
              </div>
            </div>

            <div className="relative mt-4 sm:mt-6 lg:mt-8">
              <div className="pointer-events-none absolute -top-10 -right-14 h-32 w-32 rounded-full bg-brand-highlight/20 blur-3xl" />
              <div className="pointer-events-none absolute -bottom-12 -left-16 h-36 w-36 rounded-full bg-white/10 blur-3xl" />
              <div className="relative w-full rounded-3xl border border-white/15 bg-black/70 p-6 shadow-glass sm:max-w-md sm:p-7 lg:ml-auto lg:p-9">
                <p className="text-[11px] uppercase tracking-[0.35em] text-brand-highlight/70">
                  {hero.cardEyebrow}
                </p>
                <h3 className="mt-3 text-lg font-semibold text-white sm:text-xl md:text-2xl">{hero.cardTitle}</h3>
                <p className="mt-3 text-sm text-white/70">{hero.cardDescription}</p>
                <ul className="mt-4 space-y-2 text-sm text-white/70">
                  {hero.cardBullets.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <span className="mt-1 h-2 w-2 rounded-full bg-brand-highlight/70" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-10 grid gap-6 border-t border-white/10 pt-8 sm:grid-cols-3 sm:gap-0 sm:divide-x sm:divide-white/10">
            {hero.metrics.map((metric) => (
              <div key={metric.label} className="space-y-1 py-2 sm:px-6">
                <p className="text-base font-semibold tracking-tight text-white md:text-base">{metric.value}</p>
                <p className="text-[11px] uppercase tracking-[0.2em] text-white/55">{metric.label}</p>
              </div>
            ))}
          </div>

          {hero.highlights.length > 0 ? (
            <ul className="mt-6 grid gap-4 text-xs text-white/65 sm:grid-cols-2 lg:grid-cols-3">
              {hero.highlights.map((item) => (
                <li key={item} className="flex items-start">
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </Container>
      </HeroSection>

      <Section className="py-8 lg:py-10">
        <Container>
          <div className="max-w-3xl space-y-4">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
              {locale === 'en' ? 'What we handle' : 'Τι υλοποιούμε'}
            </p>
            <h2 className="text-3xl font-semibold text-white md:text-5xl">
              {locale === 'en'
                ? 'What we take on.'
                : 'Τι αναλαμβάνουμε'}
            </h2>
            <p className="text-lg text-white/70">
              {locale === 'en'
                ? 'We cover the full journey: website, store, brand, and steady growth. Pick what you need and we form the right squad in under two weeks.'
                : 'Καλύπτουμε όλη τη διαδρομή: site, e-shop, brand και συνεχή ανάπτυξη. Διαλέξτε τι χρειάζεστε και στήνουμε την κατάλληλη ομάδα σε λιγότερο από δύο εβδομάδες.'}
            </p>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {services.map(({ key, title, icon: Icon, description, href }) => (
              <a
                key={key}
                href={href}
                className="group relative overflow-hidden rounded-2xl border border-white/15 bg-black/70 backdrop-blur-xl p-8 transition-all duration-300 hover:bg-white/10 hover:border-white/25 hover:shadow-lg hover:shadow-white/10"
              >
                <span className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/0 via-white/0 to-brand-highlight/0 opacity-0 transition-opacity duration-300 group-hover:opacity-10" />
                <div className="relative flex items-center justify-between">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white transition-colors duration-300 group-hover:border-brand-highlight/70 group-hover:text-brand-highlight">
                    <Icon className="h-6 w-6" />
                  </div>
                  <ArrowUpRight className="h-5 w-5 text-white/40 transition-colors duration-300 group-hover:text-brand-highlight" />
                </div>
                <h3 className="relative mt-6 text-2xl font-semibold text-white">{title}</h3>
                <p className="relative mt-3 text-sm text-white/70">{description}</p>
              </a>
            ))}
          </div>
        </Container>
      </Section>

      <Section className="relative overflow-hidden bg-dark-900/25">
        <div className="absolute inset-0 geometric-pattern opacity-40" />
        <Container
          className={`relative grid grid-cols-1 gap-12 ${
            caseStudy.metrics.length
              ? 'lg:grid-cols-[0.95fr_1.05fr] lg:items-center lg:gap-16'
              : 'lg:grid-cols-1 lg:gap-12'
          }`}
        >
          <div className="space-y-6">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
              {caseStudy.eyebrow}
            </p>
            <h2 className="text-3xl font-semibold text-white md:text-4xl">{caseStudy.title}</h2>
            <p className="text-base text-white/70">{caseStudy.description}</p>
            <ul className="space-y-3 text-sm text-white/70">
              {caseStudy.bullets.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <span className="mt-1 h-2 w-2 rounded-full bg-brand-highlight/70" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            {!caseStudy.metrics.length ? (
              <a
                href={localizedPath('/projects')}
                className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors"
              >
                {caseStudy.cta}
              </a>
            ) : null}
          </div>

          {caseStudy.metrics.length ? (
            <div className="relative rounded-3xl border border-white/15 bg-black/70 p-10 shadow-glass">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-3 sm:gap-0 sm:divide-x sm:divide-white/10">
                {caseStudy.metrics.map((metric) => (
                  <div key={metric.label} className="space-y-1 py-2 sm:px-6">
                    <p className="text-base font-semibold tracking-tight text-white">{metric.value}</p>
                    <p className="text-[11px] uppercase tracking-[0.35em] text-white/55">{metric.label}</p>
                  </div>
                ))}
              </div>
              <p className="mt-8 text-sm text-white/60">{caseStudy.cta}</p>
            </div>
          ) : null}
        </Container>
      </Section>

      <Section>
        <Container>
          <div className="max-w-3xl space-y-4">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
              {locale === 'en' ? 'How we deliver' : 'Πώς παραδίδουμε'}
            </p>
            <h2 className="text-3xl font-semibold text-white md:text-5xl">
              {locale === 'en'
                ? 'Simple process with clear stages.'
                : 'Απλή διαδικασία με ξεκάθαρα στάδια.'}
            </h2>
            <p className="text-lg text-white/70">
              {locale === 'en'
                ? 'You know what is happening at each phase and when we move to the next step.'
                : 'Ξέρετε τι γίνεται σε κάθε φάση και πότε προχωράμε στο επόμενο βήμα.'}
            </p>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {process.map((step, index) => (
              <div
                key={step.title}
                className="rounded-2xl border border-white/15 bg-black/70 backdrop-blur-xl p-8 shadow-glass transition-all duration-300 hover:bg-white/10 hover:border-white/25 hover:shadow-lg hover:shadow-white/10"
              >
                <span className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                  {index + 1 < 10 ? `0${index + 1}` : index + 1}
                </span>
                <h3 className="mt-3 text-xl font-semibold text-white">{step.title}</h3>
                <p className="mt-3 text-sm text-white/70">{step.description}</p>
              </div>
            ))}
          </div>
        </Container>
      </Section>

      <Section className="bg-black/40 pb-10 lg:pb-12">
        <Container>
          <div className="max-w-3xl space-y-4">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
              {locale === 'en' ? 'Collaboration' : 'ΤΡΟΠΟΣ ΣΥΝΕΡΓΑΣΙΑΣ'}
            </p>
            <h2 className="text-3xl font-semibold text-white md:text-5xl">
              {locale === 'en'
                ? 'Why teams trust us'
                : 'Γιατί μας εμπιστεύονται'}
            </h2>
            <p className="text-lg text-white/70">
              {locale === 'en'
                ? 'Partnerships last when there is transparency, solid organization, and consistent delivery.'
                : 'Οι συνεργασίες κρατάνε όταν υπάρχει διαφάνεια, σωστή οργάνωση και συνέπεια στην παράδοση.'}
            </p>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {proof.map(({ title, description, icon: Icon }) => (
              <div
                key={title}
                className="rounded-2xl border border-white/15 bg-black/70 backdrop-blur-xl p-8 transition-all duration-300 hover:bg-white/10 hover:border-white/25 hover:shadow-lg hover:shadow-white/10"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white">
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="mt-6 text-xl font-semibold text-white">{title}</h3>
                <p className="mt-3 text-sm text-white/70">{description}</p>
              </div>
            ))}
          </div>
        </Container>
      </Section>

      <Section className="py-6 lg:py-8">
        <Container>
          <div className="rounded-2xl border border-white/15 bg-black/70 backdrop-blur-xl p-6 md:p-10 shadow-glass hover:bg-white/10 hover:border-white/25 hover:shadow-lg hover:shadow-white/10">
            <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center md:text-left">
                  <p className="text-xl font-semibold text-white">{stat.value}</p>
                  <p className="text-sm uppercase tracking-[0.2em] text-white/55">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </Section>

      <Section id="projects" className="py-0">
        <Container>
          <PortfolioGallery
            eyebrow={locale === 'en' ? 'Selected' : 'Επιλεγμένα'}
            title={locale === 'en' ? 'Recent Projects' : 'Πρόσφατα Έργα'}
            compact={true}
            archiveButton={{
              text: locale === 'en' ? 'More projects' : 'Περισσότερα έργα',
              href: localizedPath('/projects'),
            }}
          />
        </Container>
      </Section>

      <Section>
        <Container>
          <div className="rounded-[32px] border border-white/15 bg-black/70 p-12 shadow-glass">
            <div className="mb-8 flex flex-col gap-3 rounded-2xl border border-white/10 bg-black/60 px-8 py-6 text-center">
              <p className="text-xs font-semibold uppercase tracking-[0.35em] text-brand-highlight/80">
                {locale === 'en' ? 'Collaboration' : 'Συνεργασία'}
              </p>
              <Display
                variant="h1"
                as="h2"
                className="mx-auto max-w-4xl text-balance text-xl font-semibold text-white md:text-3xl"
              >
                {closing.title}
              </Display>
            </div>

            <p className="mx-auto max-w-3xl text-center text-lg text-white/70">{closing.description}</p>

            <ul className="mt-8 grid gap-3 text-left text-sm text-white/70 sm:grid-cols-3 sm:gap-4">
              {closing.highlights.map((highlight) => (
                <li
                  key={highlight}
                  className="group flex items-start gap-3 rounded-2xl border border-white/5 bg-black/60 px-5 py-4 transition hover:border-white/15"
                >
                  <span className="mt-1 h-2 w-2 rounded-full bg-brand-highlight/70 transition-colors duration-300 group-hover:bg-brand-highlight" />
                  <span className="leading-relaxed">{highlight}</span>
                </li>
              ))}
            </ul>

            <div className="mt-12 flex flex-col gap-4 text-center sm:flex-row sm:justify-center">
              <Button
                as="button"
                onClick={() => window.navigateToConsultation?.()}
                trackCta={{ text: closing.primary, section: 'home_closing', variant: 'primary' }}
                variant="primary"
                size="lg"
                className="w-full justify-center sm:w-auto"
              >
                {closing.primary}
              </Button>
              <Button
                as="button"
                onClick={() => window.navigateToPortfolio?.()}
                trackCta={{ text: closing.secondary, section: 'home_closing', variant: 'secondary' }}
                variant="secondary"
                size="lg"
                className="w-full justify-center sm:w-auto"
              >
                {closing.secondary}
              </Button>
            </div>

            <p className="mt-6 text-center text-sm text-white/50">
              {locale === 'en'
                ? 'For immediate contact: '
                : 'Για άμεση επικοινωνία: '}
              <a className="text-white hover:text-brand-highlight underline decoration-white/30 hover:decoration-brand-highlight underline-offset-4" href="tel:+302351104251">
                +30 23511 04251
              </a>
            </p>
          </div>
        </Container>
      </Section>
    </main>
  );
}
